import { apiClient } from "~/utils/apiClient";

interface Page<T> {
  items: T[];
  metadata: { limit: number; offset: number; total_count: number; has_more: boolean };
}

/**
 * Fetch every item of an offset/limit paginated list by following
 * `metadata.has_more`, `pageSize` items at a time. Returns one page holding
 * all items.
 */
export async function fetchAllPages<T>(baseUrl: string, pageSize: number): Promise<Page<T>> {
  // `baseUrl` may already carry a query, so join paging params with the
  // right separator rather than assuming a bare path.
  const sep = baseUrl.includes("?") ? "&" : "?";
  const items: T[] = [];
  let offset = 0;
  for (;;) {
    const page = await apiClient<Page<T>>(`${baseUrl}${sep}limit=${pageSize}&offset=${offset}`);
    items.push(...page.items);
    offset += page.items.length;
    // Stop when the server reports no more pages, and defensively when a page
    // comes back empty so a misbehaving `has_more` can't spin forever.
    if (!page.metadata.has_more || page.items.length === 0) {
      break;
    }
  }
  return {
    items,
    metadata: { limit: pageSize, offset: 0, total_count: items.length, has_more: false },
  };
}
